/**
 * Light, dark, and system — three states, not two.
 *
 * "System" is a real choice rather than the absence of one: a user who has not
 * picked follows their OS and keeps following it when the OS changes at sunset.
 * That is why the class is applied by this store and the `prefers-color-scheme`
 * block in the token stylesheet is only the pre-script fallback.
 */

import { useEffect } from 'react';
import { create } from 'zustand';
import { mediaQuery } from '../lib/media-query';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'connect.theme';

function readStored(): ThemeMode {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system';
  } catch {
    return 'system';
  }
}

function systemPrefersDark(): boolean {
  // No `matchMedia` means no stated preference, which resolves to light — the
  // same answer as a system set to light, and the one that cannot surprise.
  return mediaQuery('(prefers-color-scheme: dark)')?.matches ?? false;
}

function applyClass(mode: ThemeMode): void {
  const dark = mode === 'dark' || (mode === 'system' && systemPrefersDark());
  document.documentElement.classList.toggle('dark', dark);
}

interface ThemeState {
  /** The user's choice. */
  preference: ThemeMode;
  /** What is actually on screen — `system` resolved. */
  mode: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
}

export const useTheme = create<ThemeState>((set) => {
  const preference = typeof window === 'undefined' ? 'system' : readStored();
  return {
    preference,
    mode: preference === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : preference,
    setMode: (next) => {
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // A theme that could not be remembered still applies for this session.
      }
      applyClass(next);
      set({
        preference: next,
        mode: next === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : next,
      });
    },
  };
});

/**
 * Applies the theme class and follows the OS while the preference is `system`.
 *
 * Mounted once near the root.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const preference = useTheme((state) => state.preference);

  useEffect(() => {
    applyClass(preference);
    if (preference !== 'system') return;

    const query = mediaQuery('(prefers-color-scheme: dark)');
    // Nothing to follow: the class applied above stands until the user picks.
    if (!query) return;
    const onChange = () => {
      applyClass('system');
      useTheme.setState({ mode: query.matches ? 'dark' : 'light' });
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [preference]);

  return <>{children}</>;
}

/**
 * A snippet to inline in `index.html`, before the app loads.
 *
 * Without it there is a flash of light theme on every cold load for a dark-mode
 * user, because the class is applied by React and React runs after paint.
 */
export const THEME_BOOT_SCRIPT = `(function(){try{var m=localStorage.getItem('connect.theme')||'system';var d=m==='dark'||(m==='system'&&window.matchMedia('(prefers-color-scheme: dark)').matches);if(d)document.documentElement.classList.add('dark');}catch(e){}})();`;
